// Firestore helpers for Events
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import type { Event, EventRSVP, EventInvite } from '../../types';
import { eventSchema, eventRSVPSchema, eventInviteSchema } from '../../schemas';

const convertTimestamps = (data: any) => {
  const converted = { ...data };
  Object.keys(converted).forEach((key) => {
    if (converted[key] instanceof Timestamp) {
      converted[key] = converted[key].toDate();
    }
  });
  return converted;
};

/**
 * Create a new event
 */
export const createEvent = async (
  eventData: Omit<Event, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> => {
  // Validate with schema (may need to adjust for partial validation)
  const eventRef = doc(collection(db, 'events'));

  // Remove undefined, null, and empty string values recursively
  const removeUndefined = (obj: any): any => {
    const cleaned: any = {};
    Object.keys(obj).forEach((key) => {
      const value = (obj as any)[key];
      if (value !== undefined && value !== null && value !== '') {
        if (typeof value === 'object' && !Array.isArray(value) && value !== null && !(value instanceof Date)) {
          const cleanedNested = removeUndefined(value);
          if (Object.keys(cleanedNested).length > 0) {
            cleaned[key] = cleanedNested;
          }
        } else {
          cleaned[key] = value;
        }
      }
    });
    return cleaned;
  };

  const cleanedData = removeUndefined(eventData);

  const event = {
    ...cleanedData,
    createdAt: new Date(),
    updatedAt: new Date(),
  };

  await setDoc(eventRef, {
    ...event,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    startsAt: Timestamp.fromDate(event.startsAt),
    endsAt: event.endsAt ? Timestamp.fromDate(event.endsAt) : null,
  });

  return eventRef.id;
};

/**
 * Get an event by ID
 */
export const getEvent = async (eventId: string): Promise<Event | null> => {
  console.log('🔵 [getEvent] Starting to fetch event:', eventId);
  try {
    console.log('🔵 [getEvent] Calling getDoc...');
    const eventDoc = await getDoc(doc(db, 'events', eventId));
    console.log('🔵 [getEvent] getDoc completed, exists?', eventDoc.exists());

    if (!eventDoc.exists()) {
      console.log('🔴 [getEvent] Event document does not exist');
      return null;
    }

    console.log('🟢 [getEvent] Event document exists, converting data...');
    const data = convertTimestamps(eventDoc.data());
    const event = {
      id: eventDoc.id,
      ...data,
    } as Event;
    console.log('🟢 [getEvent] Event data converted, returning:', event.id);
    return event;
  } catch (error: any) {
    console.error('🔴 [getEvent] ERROR fetching event:', error);
    console.error('🔴 [getEvent] Error code:', error?.code);
    console.error('🔴 [getEvent] Error message:', error?.message);
    throw error; // Re-throw so EventView can handle it
  }
};

/**
 * Update an event
 */
export const updateEvent = async (
  eventId: string,
  updates: Partial<Event>
): Promise<void> => {
  const eventRef = doc(db, 'events', eventId);

  const { id, createdAt, ...safeUpdates } = updates as any;

  // Remove undefined, null, and empty string values recursively
  const removeUndefined = (obj: any): any => {
    if (obj === null || typeof obj !== 'object' || obj instanceof Date) {
      return obj;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(removeUndefined);
    }
    
    const cleaned: any = {};
    Object.keys(obj).forEach((key) => {
      const value = obj[key];
      // Only include if value is not undefined, null, or empty string
      // But preserve boolean false, number 0, and Date objects
      if (value !== undefined && value !== null && value !== '') {
        cleaned[key] = removeUndefined(value);
      } else if (value === false || value === 0 || value instanceof Date) {
        // Preserve boolean false, number 0, and Date objects
        cleaned[key] = value;
      }
    });
    return cleaned;
  };

  // Remove undefined values
  const cleanedUpdates = removeUndefined(safeUpdates);

  // Convert Date objects to Timestamps
  if (cleanedUpdates.startsAt instanceof Date) {
    cleanedUpdates.startsAt = Timestamp.fromDate(cleanedUpdates.startsAt);
  }
  if (cleanedUpdates.endsAt instanceof Date) {
    cleanedUpdates.endsAt = Timestamp.fromDate(cleanedUpdates.endsAt);
  }

  console.log('🔵 [updateEvent] Final cleaned updates:', cleanedUpdates);
  console.log('🔵 [updateEvent] Event ID:', eventId);
  
  try {
    await updateDoc(eventRef, {
      ...cleanedUpdates,
      updatedAt: serverTimestamp(),
    });
    console.log('🟢 [updateEvent] Event updated successfully');
  } catch (error: any) {
    console.error('🔴 [updateEvent] ERROR updating event:', error);
    console.error('🔴 [updateEvent] Error code:', error?.code);
    console.error('🔴 [updateEvent] Error message:', error?.message);
    console.error('🔴 [updateEvent] Error details:', error);
    throw error; // Re-throw so EditEvent component can handle it
  }
};

/**
 * Delete an event
 */
export const deleteEvent = async (eventId: string): Promise<void> => {
  console.log('🔵 [deleteEvent] Starting to delete event:', eventId);
  try {
    const eventRef = doc(db, 'events', eventId);
    console.log('🔵 [deleteEvent] Event reference created, calling deleteDoc...');
    await deleteDoc(eventRef);
    console.log('🟢 [deleteEvent] Event deleted successfully');
  } catch (error: any) {
    console.error('🔴 [deleteEvent] ERROR deleting event:', error);
    console.error('🔴 [deleteEvent] Error code:', error?.code);
    console.error('🔴 [deleteEvent] Error message:', error?.message);
    throw error; // Re-throw so DeleteEvent component can handle it
  }
};

/**
 * Get events by host
 */
export const getEventsByHost = async (
  hostType: 'profile' | 'group',
  hostId: string
): Promise<Event[]> => {
  const q = query(
    collection(db, 'events'),
    where('hostType', '==', hostType),
    where('hostId', '==', hostId),
    orderBy('startsAt', 'asc')
  );

  const snapshot = await getDocs(q);
  const events: Event[] = [];

  snapshot.forEach((doc) => {
    const data = convertTimestamps(doc.data());
    events.push({
      id: doc.id,
      ...data,
    } as Event);
  });

  return events;
};

/**
 * Get upcoming events for a user
 */
export const getUpcomingEvents = async (
  userId: string,
  limit: number = 20
): Promise<Event[]> => {
  // This would need to query RSVPs and then fetch events
  // For now, simplified version
  const now = new Date();
  const q = query(
    collection(db, 'events'),
    where('startsAt', '>=', Timestamp.fromDate(now)),
    orderBy('startsAt', 'asc')
  );

  const snapshot = await getDocs(q);
  const events: Event[] = [];

  snapshot.forEach((doc) => {
    const data = convertTimestamps(doc.data());
    if (data.startsAt >= now) {
      events.push({
        id: doc.id,
        ...data,
      } as Event);
    }
  });

  return events.slice(0, limit);
};

/**
 * Create RSVP
 */
export const createRSVP = async (
  rsvpData: Omit<EventRSVP, 'createdAt' | 'updatedAt'>
): Promise<void> => {
  // Validate with schema (may need to adjust for partial validation)
  const rsvpRef = doc(
    db,
    'events',
    rsvpData.eventId,
    'rsvps',
    rsvpData.userId
  );

  await setDoc(rsvpRef, {
    ...rsvpData,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    checkedInAt: rsvpData.checkedInAt
      ? Timestamp.fromDate(rsvpData.checkedInAt)
      : null,
  });

  // Trigger a chat list refresh event so the chat appears for the new attendee
  // This is a client-side event that ChatList listens to
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('chatListRefresh'));
  }
};

/**
 * Get RSVP for user and event
 */
export const getRSVP = async (
  eventId: string,
  userId: string
): Promise<EventRSVP | null> => {
  try {
    const rsvpDoc = await getDoc(
      doc(db, 'events', eventId, 'rsvps', userId)
    );

    if (!rsvpDoc.exists()) {
      return null;
    }

    const data = convertTimestamps(rsvpDoc.data());
    return {
      userId: rsvpDoc.id,
      eventId,
      ...data,
    } as EventRSVP;
  } catch (error) {
    // If subcollection doesn't exist, return null
    return null;
  }
};

/**
 * Update RSVP
 */
export const updateRSVP = async (
  eventId: string,
  userId: string,
  updates: Partial<EventRSVP>
): Promise<void> => {
  const rsvpRef = doc(db, 'events', eventId, 'rsvps', userId);

  const safeUpdates: any = { ...updates };
  if (safeUpdates.checkedInAt instanceof Date) {
    safeUpdates.checkedInAt = Timestamp.fromDate(safeUpdates.checkedInAt);
  }

  // Remove fields that shouldn't be updated directly
  delete safeUpdates.userId;
  delete safeUpdates.eventId;
  delete safeUpdates.createdAt;

  await updateDoc(rsvpRef, {
    ...safeUpdates,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Get public events (for discovery)
 */
export const getPublicEvents = async (limit: number = 20): Promise<Event[]> => {
  try {
    const now = new Date();
    let snapshot;
    
    // Try query with orderBy first (requires index)
    try {
      const q = query(
        collection(db, 'events'),
        where('visibility', '==', 'public'),
        where('startsAt', '>=', Timestamp.fromDate(now)),
        orderBy('startsAt', 'asc')
      );
      snapshot = await getDocs(q);
    } catch (indexError: any) {
      // If index doesn't exist, try without orderBy and filter in memory
      console.warn('⚠️ [getPublicEvents] Index missing, querying without orderBy. Create index at:', indexError?.message?.match(/https:\/\/[^\s]+/)?.[0] || 'Firebase Console');
      const q = query(
        collection(db, 'events'),
        where('visibility', '==', 'public')
      );
      snapshot = await getDocs(q);
    }

    const events: Event[] = [];

    snapshot.forEach((doc) => {
      const data = convertTimestamps(doc.data());
      const startsAtDate = data.startsAt instanceof Date ? data.startsAt : new Date(data.startsAt);
      // Filter for future events
      if (startsAtDate >= now) {
        events.push({
          id: doc.id,
          ...data,
        } as Event);
      }
    });

    // Sort by date and randomize order
    events.sort((a, b) => {
      const aDate = a.startsAt instanceof Date ? a.startsAt : new Date(a.startsAt);
      const bDate = b.startsAt instanceof Date ? b.startsAt : new Date(b.startsAt);
      return aDate.getTime() - bDate.getTime();
    });
    
    // Randomize order
    const shuffled = events.sort(() => Math.random() - 0.5);
    return shuffled.slice(0, limit);
  } catch (error) {
    console.error('❌ [getPublicEvents] Error fetching public events:', error);
    // Return empty array on error (don't break the app)
    return [];
  }
};

/**
 * Get discoverable events (public + group_only events for user's groups + event_only events for user's parent events)
 */
export const getDiscoverableEvents = async (
  userGroupIds: string[],
  limit: number = 20,
  userEventIds: string[] = [] // Events user has RSVP'd to (for event_only visibility)
): Promise<Event[]> => {
  try {
    const now = new Date();
    const events: Event[] = [];
    
    // Get public events
    const publicEvents = await getPublicEvents(limit * 2); // Get more to account for filtering
    events.push(...publicEvents);    // Get group_only events for groups the user is a member of
    if (userGroupIds.length > 0) {
      try {
        // Query for group_only events hosted by user's groups
        const groupOnlyQuery = query(
          collection(db, 'events'),
          where('visibility', '==', 'group_only'),
          where('hostType', '==', 'group')
        );
        const groupOnlySnapshot = await getDocs(groupOnlyQuery);

        groupOnlySnapshot.forEach((doc) => {
          const data = convertTimestamps(doc.data());
          const event = {
            id: doc.id,
            ...data,
          } as Event;

          // Only include if the event is hosted by one of the user's groups
          if (event.hostType === 'group' && userGroupIds.includes(event.hostId)) {
            const startsAtDate = event.startsAt instanceof Date ? event.startsAt : new Date(event.startsAt);
            // Filter for future events
            if (startsAtDate >= now) {
              events.push(event);
            }
          }
        });
      } catch (groupOnlyError) {
        console.error('Error loading group_only events:', groupOnlyError);
      }
    }    // Get event_only events for parent events the user has RSVP'd to
    if (userEventIds.length > 0) {
      try {
        // Query for event_only events hosted by parent events
        const eventOnlyQuery = query(
          collection(db, 'events'),
          where('visibility', '==', 'event_only'),
          where('hostType', '==', 'event')
        );
        const eventOnlySnapshot = await getDocs(eventOnlyQuery);

        eventOnlySnapshot.forEach((doc) => {
          const data = convertTimestamps(doc.data());
          const event = {
            id: doc.id,
            ...data,
          } as Event;          // Only include if the event is hosted by a parent event the user has RSVP'd to
          if (event.hostType === 'event' && userEventIds.includes(event.hostId)) {
            const startsAtDate = event.startsAt instanceof Date ? event.startsAt : new Date(event.startsAt);
            // Filter for future events
            if (startsAtDate >= now) {
              events.push(event);
            }
          }
        });
      } catch (eventOnlyError) {
        console.error('Error loading event_only events:', eventOnlyError);
      }
    }

    // Remove duplicates by ID
    const uniqueEvents = Array.from(
      new Map(events.map(e => [e.id, e])).values()
    );

    // Sort by date and randomize order
    uniqueEvents.sort((a, b) => {
      const aDate = a.startsAt instanceof Date ? a.startsAt : new Date(a.startsAt);
      const bDate = b.startsAt instanceof Date ? b.startsAt : new Date(b.startsAt);
      return aDate.getTime() - bDate.getTime();
    });
    
    // Randomize order
    const shuffled = uniqueEvents.sort(() => Math.random() - 0.5);
    return shuffled.slice(0, limit);
  } catch (error) {
    console.error('❌ [getDiscoverableEvents] Error fetching discoverable events:', error);
    return [];
  }
};